/**
 * Manages a single main navigation item that contains a sub-navigation menu.
 * Handles toggling the submenu open/closed state and provides accessibility features.
 */
export class MainNavItemWithSubNav {
    /**
     * Creates a new MainNavItemWithSubNav instance
     * @param {HTMLElement} itemEl - The main navigation item element
     */
    constructor(itemEl) {
        /** @type {HTMLElement} */
        this.itemEl = itemEl;

        /** @type {HTMLElement|null} */
        this.toggle = this.itemEl.querySelector('.main-nav__toggle');

        /** @type {HTMLElement|null} */
        this.subNav = this.itemEl.querySelector(':scope > .main-nav__list');

        /** @type {boolean} */
        this._state = false;

        /** @type {boolean} */
        this._hasFocus = false;

        if (!this.toggle || !this.subNav) {
            console.warn("SEABREEZE: Could not initialize the main navigation, because some elements are missing!");
            return;
        }

        // Bind event handlers
        this._onClick = this._onClick.bind(this);
        this._onDocumentClick = this._onDocumentClick.bind(this);
        this._onEscapeKey = this._onEscapeKey.bind(this);
        this._onFocus = this._onFocus.bind(this);
        this._onBlur = this._onBlur.bind(this);

        // Get all focusable elements within this nav item
        this._focusableElements = this._getFocusableElements();

        // Setup focus tracking immediately (works on desktop AND mobile)
        this._setupFocusTracking();

        this._updateUI();
    }

    /**
     * Gets all focusable elements within the navigation item
     * @returns {HTMLElement[]} Array of focusable elements
     * @private
     */
    _getFocusableElements() {
        const selector = 'a, button, [tabindex]:not([tabindex="-1"])';
        return Array.from(this.itemEl.querySelectorAll(selector));
    }

    /**
     * Sets up focus tracking (always active, regardless of viewport)
     * @private
     */
    _setupFocusTracking() {
        // Attach focus/blur to all focusable elements
        this._focusableElements.forEach(el => {
            el.addEventListener('focus', this._onFocus);
            el.addEventListener('blur', this._onBlur);
        });

        // Listen for ESC key globally
        document.addEventListener('keydown', this._onEscapeKey);
    }

    /**
     * Tears down focus tracking
     * @private
     */
    _teardownFocusTracking() {
        this._focusableElements.forEach(el => {
            el.removeEventListener('focus', this._onFocus);
            el.removeEventListener('blur', this._onBlur);
        });

        document.removeEventListener('keydown', this._onEscapeKey);
    }

    /**
     * Enables mobile interaction (click to toggle)
     * Attaches click event listeners
     * @returns {void}
     */
    enable() {
        this.toggle.addEventListener('click', this._onClick);
    }

    /**
     * Disables mobile interaction
     * Removes click event listeners and cleans up
     * @returns {void}
     */
    disable() {
        this.toggle.removeEventListener('click', this._onClick);

        // Also remove document click listener if open
        document.removeEventListener('click', this._onDocumentClick);
    }

    /**
     * Handles click events on the toggle button (mobile only)
     * @param {MouseEvent} event - The click event
     * @private
     */
    _onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.state = !this.state;
    }

    /**
     * Handles clicks outside the navigation item to close the submenu (mobile only)
     * @param {MouseEvent} event - The click event
     * @private
     */
    _onDocumentClick(event) {
        if (!this.itemEl.contains(event.target)) {
            this.state = false;
        }
    }

    /**
     * Handles focus on any focusable element within the nav item
     * @param {FocusEvent} event - The focus event
     * @private
     */
    _onFocus(event) {
        this.hasFocus = true;
    }

    /**
     * Handles blur on any focusable element
     * Uses setTimeout to check if focus moved to another element within this nav item
     * @param {FocusEvent} event - The blur event
     * @private
     */
    _onBlur(event) {
        // Use setTimeout to let the new focus target be set
        setTimeout(() => {
            // Check if the currently focused element is still within this nav item
            const focusedElement = document.activeElement;
            if (!this.itemEl.contains(focusedElement)) {
                this.hasFocus = false;
            }
        }, 0);
    }

    /**
     * Handles ESC key press to close the submenu
     * Works on both desktop (hover) and mobile (click) navigation
     * @param {KeyboardEvent} event - The keyboard event
     * @private
     */
    _onEscapeKey(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
            // Close if:
            // - Expanded in mobile mode (this._state is true), OR
            // - Has focus in desktop mode (for hover-based navigation)
            if (this._state || this._hasFocus) {
                event.preventDefault();

                // In mobile mode, close and return focus
                if (this._state) {
                    this.state = false;
                    this.toggle.focus();
                } else {
                    // In desktop mode with hover, just clear focus state
                    this.hasFocus = false;
                }
            }
        }
    }

    /**
     * Sets the expanded state of the sub-navigation (mobile behavior)
     * Adds or removes global event listeners depending on state
     * @param {boolean} value - The new state value
     */
    set state(value) {
        const newValue = Boolean(value);

        if (this._state === newValue) return;

        this._state = newValue;

        if (this._state) {
            requestAnimationFrame(() => {
                document.addEventListener('click', this._onDocumentClick);
            });
        } else {
            document.removeEventListener('click', this._onDocumentClick);
        }

        this._updateUI();
    }

    /**
     * Gets the current expanded state
     * @returns {boolean} True if sub-navigation is expanded
     */
    get state() {
        return this._state;
    }

    /**
     * Sets the focus state
     * @param {boolean} value - Whether the nav item has focus
     */
    set hasFocus(value) {
        const newValue = Boolean(value);

        if (this._hasFocus === newValue) return;

        this._hasFocus = newValue;
        this._updateUI();
    }

    /**
     * Gets the current focus state
     * @returns {boolean} True if nav item or its children have focus
     */
    get hasFocus() {
        return this._hasFocus;
    }

    /**
     * Updates the UI to reflect the current state
     * @private
     */
    _updateUI() {
        this.itemEl.setAttribute('data-has-expanded-sub-nav', String(this._state));
        this.itemEl.setAttribute('data-has-focus', String(this._hasFocus));
        this.toggle.setAttribute('aria-expanded', String(this._state));
    }

    /**
     * Closes the sub-navigation
     * @returns {void}
     */
    close() {
        this.state = false;
    }

    /**
     * Cleanup method - call when destroying instance
     * @returns {void}
     */
    destroy() {
        this.disable();
        this._teardownFocusTracking();
    }
}
